import Lightning from '@lightningjs/sdk/src/Lightning';
import AbstractPlayerEngine from 'components/pages/playback/player-engines/AbstractPlayerEngine';
import { Ad, CustomPlayerEvent } from 'types/player';
import { roundMilliseconds } from 'support/generalUtils';
import AbstractAdManager, {
  AdCuePoint,
} from 'components/pages/playback/ad-managers/AbstractAdManager';

const SEEK_OFFSET = 0.5;
const RESUME_SECONDS_BEFORE_AD = 10;

interface AbstractPlayerTemplateSpec extends Lightning.Component.TemplateSpec {
  videoId: string | undefined;
  hasDrm: boolean | undefined;
  drmLicenseUrl: string | undefined;
  startTime: number | undefined;
  liveStreamUrl: string | null;
  liveStreamType: string;
  adZone: string;
  absoluteX?: number;
  absoluteY?: number;
  beforeLoad: ((videoEl: HTMLVideoElement) => void) | undefined;
  onStreamAdaption: ((bandWidth: number) => void) | undefined;
  onStreamLoaded: ((url: string) => void) | undefined;
  onStreamError: ((isFatal: boolean, message: string) => void) | undefined;
  onPlaybackError: ((isFatal: boolean, message: string) => void) | undefined;
}

export default abstract class AbstractPlayer<
  TemplateSpec extends Lightning.Component.TemplateSpec = Lightning.Component.TemplateSpec,
> extends Lightning.Component<AbstractPlayerTemplateSpec & TemplateSpec> {
  videoElement: HTMLMediaElement | undefined;

  protected _engine: AbstractPlayerEngine | undefined;
  protected _beforeLoad: AbstractPlayerTemplateSpec['beforeLoad'];
  protected _onStreamAdaption: AbstractPlayerTemplateSpec['onStreamAdaption'];
  protected _onStreamLoaded: AbstractPlayerTemplateSpec['onStreamLoaded'];
  protected _onStreamError: AbstractPlayerTemplateSpec['onStreamError'];
  protected _onPlaybackError: AbstractPlayerTemplateSpec['onPlaybackError'];

  abstract readonly playerType: 'lightning' | 'brightcove';
  protected abstract _AdManager: AbstractAdManager;
  protected Consumer: Lightning.Component | null = null;

  // Content position values
  protected _preSeekContentTime: number | null = null;
  protected _snapForwardRawTime: number | null = null;

  // These positions are relative to the entire page instead of the parent component
  protected _absoluteX: number | undefined;
  protected _absoluteY: number | undefined;

  protected _ccSuppressed = false;
  protected _isClosed = false;

  videoId: string | undefined;
  hasDrm: boolean | undefined;
  drmLicenseUrl: string | undefined;
  startTime: number | undefined;
  liveStreamUrl: string | null = null;
  liveStreamType = '';
  adZone = '';

  set beforeLoad(beforeLoad: AbstractPlayerTemplateSpec['beforeLoad']) {
    this._beforeLoad = beforeLoad;
  }

  set onStreamAdaption(
    onStreamAdaption: AbstractPlayerTemplateSpec['onStreamAdaption'],
  ) {
    this._onStreamAdaption = onStreamAdaption;
  }

  set onStreamLoaded(
    onStreamLoaded: AbstractPlayerTemplateSpec['onStreamLoaded'],
  ) {
    this._onStreamLoaded = onStreamLoaded;
  }

  set onPlaybackError(
    onPlaybackError: AbstractPlayerTemplateSpec['onPlaybackError'],
  ) {
    this._onPlaybackError = onPlaybackError;
  }

  set onStreamError(
    onStreamError: AbstractPlayerTemplateSpec['onStreamError'],
  ) {
    this._onStreamError = onStreamError;
  }

  set ccSuppressed(ccSuppressed: boolean) {
    this._ccSuppressed = ccSuppressed;
    if (!this._engine) return;

    this._engine.ccSuppressed = ccSuppressed;
    this._engine.applyCcPreference();
  }

  set absoluteX(absoluteX: number | undefined) {
    this._absoluteX = absoluteX;

    this.updateVideoElemDimensions({ x: absoluteX });
  }

  set absoluteY(absoluteY: number | undefined) {
    this._absoluteY = absoluteY;

    this.updateVideoElemDimensions({ y: absoluteY });
  }

  override set w(w: number) {
    super.w = w;

    this.updateVideoElemDimensions({ w });
  }

  override get w() {
    return super.w;
  }

  override set h(h: number) {
    super.h = h;

    this.updateVideoElemDimensions({ h });
  }

  override get h() {
    return super.h;
  }

  override _active(): void {
    this._AdManager.patch({ getRawTime: this.currentRawTime.bind(this) });
  }

  open(): void {
    this._isClosed = false;
  }

  close(): void {
    this._isClosed = true;
  }

  unload(): void {
    // No-op;
  }

  reload(startTime?: number): void {
    // No-op;
  }

  abstract isPlaying(): boolean;

  abstract resume(): void;

  abstract pause(): void;

  abstract currentRawTime(): number;

  abstract currentContentTime(): number;

  protected abstract createManager(videoEl: HTMLVideoElement): void;

  protected abstract rawSeek(seekTime: number): void;

  protected abstract updateVideoElemDimensions(dimensions: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
  }): void;

  abstract streamTimeForContentTime(time: number): number | undefined;

  abstract contentTimeForStreamTime(time: number): number | undefined;

  abstract previousCuePointForStreamTime(time: number): AdCuePoint | undefined;

  abstract extractAd(event: unknown): Ad | undefined;

  protected onSpeakEventHandler(isSpeaking: boolean) {
    this._engine?.updateTtsPlaybackVolume(isSpeaking);
  }

  protected firePlayerEvent(...args: unknown[]) {
    (this.Consumer as any)?.fire('$videoPlayerEvent', ...args);
  }

  get isClosed(): boolean {
    return this._isClosed;
  }

  initialize(consumer: Lightning.Component): void {
    this.Consumer = consumer;
  }

  seekPause() {
    this.firePlayerEvent(CustomPlayerEvent.SEEK_PAUSE);
    this.pause();
  }

  seek(seekTime: number) {
    if (this.liveStreamUrl) {
      this.rawSeek(seekTime);
      return;
    }

    this.firePlayerEvent(CustomPlayerEvent.BEFORE_SEEK);

    // TODO: Handle disabling snapback for X amount of time

    const prerollCuePoint = this._AdManager.getCuePoint(1);
    // Prevent user from seeking past pre-roll if video has just started
    if (!this.startTime && prerollCuePoint && !prerollCuePoint.played) return;

    const streamSeekTime = this.streamTimeForContentTime(
      seekTime > 0 ? seekTime : 0,
    )!;
    const previousCuePoint =
      this._AdManager.getPreviousCuePoint(streamSeekTime);
    const { start: adStart = 0, played: adPlayed } = previousCuePoint ?? {};

    // If user has already partially watched content, no need to rewatch pre-roll
    const hasWatchedPreroll = !!this.startTime && adStart === 0;
    const isPreviousAdPlayed = !previousCuePoint || adPlayed;

    if (hasWatchedPreroll || isPreviousAdPlayed) {
      this.rawSeek(streamSeekTime);
    } else {
      this._preSeekContentTime = this.currentContentTime();
      this._snapForwardRawTime = streamSeekTime;
      this.rawSeek(adStart);
    }
  }

  getSaveTime(): number {
    // Save pre-seek time if available (indicating user seeked into an ad)
    const saveTime = this._preSeekContentTime ?? this.currentContentTime();

    const streamTime = this.getStreamTimeForContentTime(saveTime);
    const nextCuePoint =
      streamTime && this._AdManager.getNextCuePointFrom(streamTime);
    if (nextCuePoint) {
      const resumeTime =
        (this.getContentTimeForStreamTime(nextCuePoint.start) ?? 0) -
        RESUME_SECONDS_BEFORE_AD;
      if (saveTime > resumeTime && resumeTime > 0) {
        // Save resumeTime to watch history when exiting during an ad pod
        return resumeTime;
      }
    }

    return saveTime;
  }

  currentAdTime() {
    return this._AdManager.currentAdTime();
  }

  /** returns current ad time if ad is playing or content time if content is playing in seconds
   *
   * @returns current ad time or content time in seconds
   */
  getRelativeTime() {
    return this.isPlayerInAdPod()
      ? this.currentAdTime()
      : this.currentContentTime();
  }

  getContentTimeForStreamTime(streamTime: number): number | null {
    return this.contentTimeForStreamTime(streamTime) ?? null;
  }

  getStreamTimeForContentTime(contentTime: number): number | null {
    return this.streamTimeForContentTime(contentTime) ?? null;
  }

  adDuration() {
    return this._AdManager.adDuration();
  }

  getRemainingAdTime() {
    return this._AdManager.getRemainingAdTime();
  }

  isAdPreroll() {
    return this._AdManager.isAdPreroll();
  }

  shouldSkipAdPod(hasStartedAds: boolean) {
    return this._AdManager.shouldSkipAdPod(hasStartedAds);
  }

  shouldEarlySkipAdPod() {
    if (this.playerType === 'brightcove')
      return this._AdManager.shouldEarlySkipAdPod();

    return false;
  }

  /**
   * Skips the current ad pod by seeking to ad pod's end
   */

  skipAdPod() {
    const cuePoint = this._AdManager.getCurrentCuePoint();
    if (!cuePoint) return;

    let roundedEndTime = 0;
    if (this.playerType === 'brightcove') {
      // BC seems to need bit more offset, otherwise it gets stuck at loading.
      roundedEndTime = cuePoint.end + SEEK_OFFSET;
    } else {
      // rounds up the milliseconds in the end time
      roundedEndTime = roundMilliseconds(cuePoint.end);
    }
    this.rawSeek(roundedEndTime);

    this.firePlayerEvent(CustomPlayerEvent.SKIP_AD_POD);
  }

  /**
   * Skips single ad in the current ad pod
   */
  skipCurrentAd() {
    const skipTime = this._AdManager.currentAdTimeRemaining();
    this.rawSeek(this.currentRawTime() + skipTime);
  }

  earlySkipAdPod() {
    const cuePoint = this._AdManager.getNextCuePoint();
    if (!cuePoint) return;
    const roundedEndTime = cuePoint.end;
    this.rawSeek(roundedEndTime);
  }

  handleAdPodEnded() {
    this._AdManager.adPodEnded();
    this.snapForward();
  }

  snapForward() {
    if (this._snapForwardRawTime === null) return;

    this.rawSeek(this._snapForwardRawTime);
    this._preSeekContentTime = null;
    this._snapForwardRawTime = null;
  }

  isPlayerInAdPod() {
    return this._AdManager.isPlayerInAdPod();
  }

  shouldCleanupAdPod(hasStartedAds: boolean) {
    return this._AdManager.shouldCleanupAdPod(hasStartedAds);
  }

  cleanupAdPod() {
    this.firePlayerEvent(CustomPlayerEvent.CLEANUP_AD_POD);
  }

  getBitRate(): number | null {
    return this._engine?.currentBitrate ?? null;
  }

  disableCc() {
    this._engine?.disableCc();
  }

  enableCc(lang: string) {
    this._engine?.enableCc(lang);
  }

  getSubtitles() {
    return this._engine?.getSubtitles() ?? [];
  }

  getCcIndex(lang: string) {
    return this._engine?.getCcIndex(lang) ?? -1;
  }

  getAudioOptions() {
    return this._engine?.getAudioOptions() ?? [];
  }

  setAudioTrack(lang: string) {
    this._engine?.setAudioTrack(lang);
  }

  handleError(error: Error | string) {
    let isFatal: boolean;
    let errorMessage;

    if (error instanceof Error) {
      isFatal = true;
      errorMessage = error.message;
    } else {
      isFatal = true;
      errorMessage = error;
    }

    console.error(errorMessage, error);
    this._onPlaybackError?.(isFatal, errorMessage);
  }

  getCurrentCuePoint() {
    return this._AdManager.getCurrentCuePoint();
  }

  getLiveSyncPosition(): number | null {
    return this._engine?.getLiveSyncPosition() ?? null;
  }

  getTargetLatency(): number {
    return this._engine?.getTargetLatency() ?? 0;
  }

  verifyCuepointPlaying(rawTime: number) {
    // No-op
  }
}
